import React from 'react';
import ArtworkElement from './ArtworkElement';

// Helper to convert inches to feet and inches
const inchesToFeetInches = (inches) => {
  const feet = Math.floor(inches / 12);
  const remainingInches = Math.round(inches % 12);
  if (feet === 0) return `${remainingInches}"`;
  if (remainingInches === 0) return `${feet}'`;
  return `${feet}'${remainingInches}"`;
};

const WallComponent = ({ 
  wall, 
  index, 
  totalWalls, 
  onDrop, 
  onEdit, 
  onDelete, 
  onMoveUp, 
  onMoveDown, 
  onRemoveArtwork,
  onRepositionArtwork,
  onNudgeArtwork,
  onEditDimensions,
  onShowMeasurements,
  scale,
  getOptimizedImageUrl,
  audioMap
}) => {
  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  // Calculate proper height based on aspect ratio
  const wallWidth = parseFloat(wall.width || 120);
  const wallHeight = parseFloat(wall.height || 96);
  
  // If we have a min-width of 800px, we should scale height proportionally
  const displayWidth = Math.max(800, wallWidth * scale);
  const aspectRatio = wallHeight / wallWidth;
  const displayHeight = displayWidth * aspectRatio;
  
  // Calculate scale factors for positioning artwork
  const scaleX = displayWidth / wallWidth;
  const scaleY = displayHeight / wallHeight;
  
  // Eye level line position (57" from floor is museum standard, assuming wall starts at floor)
  // If the wall height is less than 57", don't show the line
  const eyeLevelHeight = 57; // inches from floor
  const eyeLevelFromTop = wallHeight - eyeLevelHeight; // distance from top of wall
  const showEyeLevel = eyeLevelFromTop > 0 && eyeLevelFromTop < wallHeight;
  const eyeLevelPosition = eyeLevelFromTop * scaleY; // convert to pixels
  
  return (
    <div className="wall-container">
      {/* Wall Controls */}
      <div className="wall-controls">
        {/* Updated wall name structure with better spacing */}
        <div className="wall-name-container">
          <div className="wall-title">
            {wall.source || `Wall ${wall.element_id}`}
          </div>
          <div className="wall-dimensions">
            {inchesToFeetInches(wallWidth)} × {inchesToFeetInches(wallHeight)}
            <span style={{ marginLeft: '8px', color: '#9ca3af', fontSize: '11px' }}>({wallWidth}" × {wallHeight}")</span>
          </div>
        </div>
        
        {/* Control buttons - now properly contained */}
        <div className="control-buttons-group">
          <button 
            className="wall-control-btn" 
            disabled={index === 0}
            onClick={onMoveUp}
            title="Move wall up"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>
          
          <button 
            className="wall-control-btn" 
            disabled={index === totalWalls - 1}
            onClick={onMoveDown}
            title="Move wall down"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          <button 
            className="wall-control-btn" 
            onClick={onEdit}
            title="Edit wall"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          
          <button 
            className="wall-control-btn" 
            onClick={onDelete}
            title="Delete wall"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      </div>
      
      {/* Wall Element */}
      <div 
        className="wall"
        id={`wall-${wall.element_id}`}
        data-id={wall.element_id}
        data-scale-x={scaleX}
        data-scale-y={scaleY}
        style={{
          width: `${displayWidth}px`,
          height: `${displayHeight}px`
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => {
          // Check if this is a reposition of existing artwork or a new drop
          const draggedElementId = e.dataTransfer.getData('text/plain');
          console.log('🖱️ Wall drop detected:', { draggedElementId, wallId: wall.element_id, wallName: wall.source });
          
          const isRepositioning = wall.artwork?.some(art => art.element_id.toString() === draggedElementId);
          console.log('🖱️ Is repositioning:', isRepositioning, 'Artwork on this wall:', wall.artwork?.map(a => a.element_id) || []);
          
          if (isRepositioning) {
            // This is repositioning existing artwork on this wall
            console.log('🖱️ Calling reposition handler');
            onRepositionArtwork(e, draggedElementId, wall.element_id);
          } else {
            // This is dropping new artwork from inventory
            console.log('🖱️ Calling new drop handler');
            onDrop(e, wall.element_id);
          }
        }}
      >
        {/* Eye Level Line */}
        {showEyeLevel && (
          <div
            style={{
              position: 'absolute',
              top: `${eyeLevelPosition}px`,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: 'rgba(59, 130, 246, 0.5)',
              borderTop: '1px dashed rgba(59, 130, 246, 0.7)',
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '-10px',
                fontSize: '10px',
                color: 'rgba(59, 130, 246, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '1px 4px',
                borderRadius: '2px',
                whiteSpace: 'nowrap'
              }}
            >
              Eye Level (57")
            </span>
          </div>
        )}
        
        {/* Artwork on this wall */}
        {wall.artwork && wall.artwork.map(art => (
          <ArtworkElement 
            key={art.element_id}
            artwork={art}
            scaleX={scaleX}
            scaleY={scaleY}
            onRemove={onRemoveArtwork}
            onNudge={onNudgeArtwork ? (elementId, dx, dy) => onNudgeArtwork(elementId, wall.element_id, dx, dy) : null}
            onEditDimensions={onEditDimensions}
            onShowMeasurements={onShowMeasurements ? (artwork) => onShowMeasurements(artwork, wallWidth, wallHeight, wall.artwork) : null}
            getOptimizedImageUrl={getOptimizedImageUrl}
            audioMap={audioMap}
            wallHeight={wallHeight}
            wallWidth={wallWidth}
          />
        ))}

      </div>
    </div>
  );
};

export default WallComponent;